import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import {
  initializeSocket,
  disconnectSocket,
  onNotificationCount,
  onNotificationsResponse,
  onNewNotification,
  onJoinedRoom,
  fetchNotificationsSocket
} from '../services/socketService';
import { normalizeCounts, normalizeNotification } from '../services/notificationService';

const NotificationContext = createContext();

const defaultCounts = { all: 0, unread: 0, read: 0, unviewed: 0 };

export const NotificationProvider = ({ children }) => {
  const [notificationCount, setNotificationCount] = useState(defaultCounts);
  const [newNotifications, setNewNotifications] = useState([]);
  const [isConnected, setIsConnected] = useState(false);
  const [recentNotifications, setRecentNotifications] = useState([]);
  const recentNotificationsRef = useRef([]);

  useEffect(() => {
    try {
      // Initialize socket FIRST to ensure it exists
      const socket = initializeSocket();

      // Register listeners IMMEDIATELY after socket initialization but BEFORE any emissions
      const unsubCount = onNotificationCount((counts) => {
        console.log('NotificationContext: Raw notification-count event received:', counts);
        if (counts && typeof counts === 'object') {
          // Use server-provided counts directly — do NOT merge with local list
          // so that unviewed count is authoritative (drives the blink in Topbar)
          const normalized = normalizeCounts(counts);
          console.log('NotificationContext: Normalized counts:', normalized);
          setNotificationCount(normalized);
        }
      }, socket);

      const unsubResponse = onNotificationsResponse((data) => {
        const items = Array.isArray(data?.data) ? data.data.map(normalizeNotification) : [];
        recentNotificationsRef.current = items;
        setRecentNotifications(items);
        setNotificationCount(normalizeCounts(data?.meta?.counts || {}, items));
      }, socket);

      const unsubNew = onNewNotification((notification) => {
        const normalized = normalizeNotification(notification);
        setNewNotifications((prev) => [normalized, ...prev].slice(0, 50));
        setNotificationCount((prev) => ({
          ...prev,
          all: (prev.all || 0) + 1,
          unread: (prev.unread || 0) + 1,
          unviewed: (prev.unviewed || 0) + 1
        }));
      }, socket);

      const unsubJoined = onJoinedRoom(() => {
        fetchNotificationsSocket(10, 0);
      }, socket);

      const handleConnect = () => {
        setIsConnected(true);
      };

      const handleDisconnect = () => {
        setIsConnected(false);
      };

      if (socket.connected) {
        handleConnect();
        fetchNotificationsSocket(10, 0);
      } else {
        socket.on('connect', handleConnect);
      }

      socket.on('disconnect', handleDisconnect);

      return () => {
        unsubCount();
        unsubResponse();
        unsubNew();
        unsubJoined();
        socket.off('connect', handleConnect);
        socket.off('disconnect', handleDisconnect);
        disconnectSocket();
      };
    } catch (error) {
      console.error('Failed to initialize socket:', error);
      return () => {};
    }
  }, []);

  const updateNotificationCount = useCallback((counts) => {
    setNotificationCount(normalizeCounts(counts, recentNotificationsRef.current));
  }, []);

  const clearNewNotifications = useCallback(() => {
    setNewNotifications([]);
  }, []);

  return (
    <NotificationContext.Provider
      value={{
        notificationCount,
        newNotifications,
        recentNotifications,
        isConnected,
        updateNotificationCount,
        clearNewNotifications
      }}
    >
      {children}
    </NotificationContext.Provider>
  );
};

export const useNotifications = () => {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error('useNotifications must be used within NotificationProvider');
  }
  return context;
};
